// Normalises optional limit/offset params into stable defaults.
// Replaces the `params.limit ?? 50; params.offset ?? 0` pair repeated across
// every paginated admin hook (users, trades, plans, payments, …).

export interface PaginationParams {
  readonly limit?: number;
  readonly offset?: number;
}

export interface PaginationDefaults {
  readonly limit: number;
  readonly offset?: number;
}

export interface NormalizedPagination {
  readonly [key: string]: number;
  readonly limit: number;
  readonly offset: number;
}

export interface PaginationPreset {
  readonly default: number;
  readonly max: number;
}

export const STANDARD_PRESET: PaginationPreset = { default: 50, max: 200 };
export const LARGE_PRESET: PaginationPreset = { default: 100, max: 500 };
export const LOGS_PRESET: PaginationPreset = { default: 50, max: 100 };

/**
 * Apply default limit/offset when caller leaves them unset.
 * `offset` defaults to 0 unless overridden via `defaults.offset`.
 *
 * @example
 *   const { limit, offset } = normalizePagination(params, { limit: 50 });
 */
export function normalizePagination(
  params: PaginationParams,
  defaults: PaginationDefaults,
  preset: PaginationPreset = STANDARD_PRESET,
): NormalizedPagination {
  const defaultLimit = defaults.limit ?? preset.default;
  return {
    limit: clampInteger(params.limit ?? defaultLimit, 1, preset.max),
    offset: clampInteger(
      params.offset ?? defaults.offset ?? 0,
      0,
      Number.MAX_SAFE_INTEGER,
    ),
  };
}

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.floor(value)));
}
